import { useState } from 'react';
import { useCompStructure, useOrgDepartments, type CompStructureCell } from '../../../hooks';
import { useConfirm } from '../../../components/ui/confirm';
import { useToast } from '../../../components/ui/toast';
import { Modal } from '../../../components/ui/Modal';
import { inputCls, btnPrimary, btnGhost, thb, Field, Select, EmptyState, TableShell } from './_shared';

/** โครงสร้างเงินเดือน — pay grid of (department × level) cells, AI-fillable. */

const STRATEGY_LABEL: Record<string, string> = {
  fair: 'เท่าตลาด (กลางๆ)',
  aggressive: 'นำตลาด (จ่ายสูง)',
  lean: 'ประหยัด (จ่ายต่ำกว่า)',
};

export function StructureTab() {
  const structure = useCompStructure();
  const departments = useOrgDepartments();
  const confirm = useConfirm();
  const toast = useToast();
  const deps = departments.data ?? [];
  const cells = structure.data ?? [];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CompStructureCell | null>(null);
  const [deptId, setDeptId] = useState('');
  const [level, setLevel] = useState('1');
  const [min, setMin] = useState('');
  const [mid, setMid] = useState('');
  const [max, setMax] = useState('');
  const [aiOpen, setAiOpen] = useState(false);
  const [aiStrategy, setAiStrategy] = useState('fair');

  const depName = (id: string) => deps.find((d) => d.id === id)?.name ?? '—';

  function openNew() { setEditing(null); setDeptId(''); setLevel('1'); setMin(''); setMid(''); setMax(''); setOpen(true); }
  function openEdit(c: CompStructureCell) {
    setEditing(c); setDeptId(c.department_id); setLevel(String(c.level));
    setMin(c.min_salary != null ? String(c.min_salary) : '');
    setMid(c.q2_salary != null ? String(c.q2_salary) : '');
    setMax(c.max_salary != null ? String(c.max_salary) : '');
    setOpen(true);
  }
  function onSubmit() {
    if (!deptId) { toast.error('เลือกแผนก'); return; }
    const lvl = Number(level);
    if (!Number.isInteger(lvl) || lvl < 1 || lvl > 10) { toast.error('ระดับต้องเป็น 1-10'); return; }
    const mn = Number(min), mx = Number(max);
    if (![mn, mx].every((n) => Number.isFinite(n) && n >= 0)) { toast.error('กรอกเงินเดือน ต่ำสุด/สูงสุด'); return; }
    if (mn > mx) { toast.error('ต่ำสุดต้อง ≤ สูงสุด'); return; }
    structure.upsert.mutate(
      { department_id: deptId, level: lvl, min_salary: mn, q2_salary: mid ? Number(mid) : null, max_salary: mx },
      {
        onSuccess: () => { toast.success('บันทึกแล้ว'); setOpen(false); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
      },
    );
  }
  async function onDelete(c: CompStructureCell) {
    if (!(await confirm({ title: `ลบช่อง ${depName(c.department_id)} ระดับ ${c.level}?`, danger: true, confirmText: 'ลบ' }))) return;
    structure.del.mutate(c.id, {
      onSuccess: () => toast.success('ลบแล้ว'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ'),
    });
  }
  function runAi() {
    structure.aiFill.mutate(
      { strategy: aiStrategy },
      {
        onSuccess: (r) => { toast.success(r.upserted ? `AI สร้าง/ปรับ ${r.upserted} ช่อง` : 'AI ยังไม่ได้สร้างช่องใหม่'); setAiOpen(false); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'AI ไม่สำเร็จ'),
      },
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">ตารางเงินเดือนตามแผนก × ระดับ — ใช้เป็นแนวอ้างอิงในการตั้งเงินเดือน</p>
        <div className="flex gap-2">
          <button onClick={() => setAiOpen(true)} className={btnGhost}>ให้ AI ช่วยร่าง</button>
          <button onClick={openNew} className={btnPrimary}>+ เพิ่มช่อง</button>
        </div>
      </div>

      {structure.isLoading ? (
        <p className="py-6 text-center text-sm text-gray-400">กำลังโหลด…</p>
      ) : cells.length === 0 ? (
        <EmptyState text="ยังไม่มีโครงสร้างเงินเดือน — เพิ่มเอง หรือให้ AI ช่วยร่าง" />
      ) : (
        <TableShell head={
          <tr>
            <th className="px-3 py-2 font-medium">แผนก</th>
            <th className="px-3 py-2 font-medium">ระดับ</th>
            <th className="px-3 py-2 font-medium">ต่ำสุด</th>
            <th className="px-3 py-2 font-medium">กลาง</th>
            <th className="px-3 py-2 font-medium">สูงสุด</th>
            <th className="px-3 py-2"></th>
          </tr>
        }>
          {cells.map((c) => (
            <tr key={c.id} className="border-t border-gray-100">
              <td className="px-3 py-2 font-medium text-gray-900">{depName(c.department_id)}</td>
              <td className="px-3 py-2 text-gray-500">L{c.level}</td>
              <td className="px-3 py-2 text-gray-500">{thb(c.min_salary)}</td>
              <td className="px-3 py-2 text-gray-500">{thb(c.q2_salary)}</td>
              <td className="px-3 py-2 text-gray-500">{thb(c.max_salary)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <button onClick={() => openEdit(c)} className="text-teal-700 hover:underline">แก้ไข</button>
                <button onClick={() => void onDelete(c)} className="ml-3 text-red-600 hover:underline">ลบ</button>
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? 'แก้ไขช่องเงินเดือน' : 'เพิ่มช่องเงินเดือน'}>
        <div className="space-y-3">
          <Field label="แผนก"><Select value={deptId} onChange={setDeptId} options={deps} placeholder="— เลือกแผนก —" /></Field>
          <Field label="ระดับ (1-10)"><input className={inputCls} type="number" min={1} max={10} value={level} onChange={(e) => setLevel(e.target.value)} /></Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="ต่ำสุด"><input className={inputCls} type="number" value={min} onChange={(e) => setMin(e.target.value)} /></Field>
            <Field label="กลาง"><input className={inputCls} type="number" value={mid} onChange={(e) => setMid(e.target.value)} /></Field>
            <Field label="สูงสุด"><input className={inputCls} type="number" value={max} onChange={(e) => setMax(e.target.value)} /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setOpen(false)} className={btnGhost}>ยกเลิก</button>
            <button onClick={onSubmit} disabled={structure.upsert.isPending} className={btnPrimary}>บันทึก</button>
          </div>
        </div>
      </Modal>

      <Modal open={aiOpen} onClose={() => setAiOpen(false)} title="ให้ AI ช่วยร่างโครงสร้างเงินเดือน">
        <div className="space-y-3">
          <p className="text-sm text-gray-500">AI จะสร้างตารางเงินเดือนจากแผนก/ตำแหน่งที่มี ตามกลยุทธ์ที่เลือก แล้วบันทึกให้ทันที (แก้ไขเองได้ภายหลัง)</p>
          <Field label="กลยุทธ์ค่าตอบแทน">
            <select className={inputCls} value={aiStrategy} onChange={(e) => setAiStrategy(e.target.value)}>
              <option value="fair">เท่าตลาด (กลางๆ)</option>
              <option value="aggressive">นำตลาด (จ่ายสูง)</option>
              <option value="lean">ประหยัด (จ่ายต่ำกว่า)</option>
            </select>
          </Field>
          <p className="text-xs text-gray-400">กลยุทธ์ที่เลือก: {STRATEGY_LABEL[aiStrategy]}</p>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setAiOpen(false)} className={btnGhost}>ยกเลิก</button>
            <button onClick={runAi} disabled={structure.aiFill.isPending} className={btnPrimary}>{structure.aiFill.isPending ? 'กำลังร่าง…' : 'ร่างด้วย AI'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
